import React from 'react';
import { useTranslation } from 'react-i18next';

import styles from './ReflowTooltip.module.scss';

// react-i18next returns the key when no translation is found. Use the English
// fallback in dev so non-translated builds still read as plain English.
function tr(t: (k: string) => string, key: string, fallback: string): string {
  const value = t(key);
  return value === key ? fallback : value;
}

/**
 * Animated demo of the reflow trigger. Each digit of the Phone value erases
 * right-to-left (like the buyer is hitting backspace), and once the value
 * is empty the entire row collapses, letting Mobile slide up to fill the
 * slot. Then the cycle resets. Pure CSS keyframes — no video asset.
 *
 * Animation cycle (6s loop), all class names from ReflowTooltip.module.scss:
 *   0%   – 15%  Phone value full ("555-0100") held
 *   15%  – 28%  Digits erase right-to-left, one every ~1.5% (~90 ms)
 *   28%  – 35%  Empty Phone row still visible (brief pause)
 *   35%        Phone row collapses; Mobile slides up via flex reflow
 *   35%  – 85%  Collapsed state held
 *   85%  – 90%  Phone row reopens (still empty)
 *   90%  – 95%  Digits all re-appear (snap, not staggered, to keep loop fast)
 *   95%  – 100% Held; loops to 0%
 */
function ReflowDemo() {
  return (
    <div className={styles.demo}>
      <div className={styles.line}>John Smith</div>
      <div className={`${styles.line} ${styles.phoneRow}`}>
        <span>Phone:&nbsp;</span>
        <span className={`${styles.digit} ${styles.d1}`}>5</span>
        <span className={`${styles.digit} ${styles.d2}`}>5</span>
        <span className={`${styles.digit} ${styles.d3}`}>5</span>
        <span className={`${styles.digit} ${styles.d4}`}>-</span>
        <span className={`${styles.digit} ${styles.d5}`}>0</span>
        <span className={`${styles.digit} ${styles.d6}`}>1</span>
        <span className={`${styles.digit} ${styles.d7}`}>0</span>
        <span className={`${styles.digit} ${styles.d8}`}>0</span>
      </div>
      <div className={styles.line}>Mobile: 555-0199</div>
    </div>
  );
}

function ReflowTooltip() {
  const { t } = useTranslation();
  return (
    <div className={styles.overlay}>
      <ReflowDemo />
      <div className={styles.container}>
        <div className={styles.title}>{tr(t, 'editor-add-reflow', 'Add reflow')}</div>
        <div className={styles.description}>
          {tr(
            t,
            'editor-add-reflow-description',
            'Group stacked text elements. When the buyer leaves an editable line blank, that line is hidden and the lines below shift up to close the gap.',
          )}
        </div>
        <div className={styles.hint}>
          {tr(t, 'editor-add-reflow-hint', 'Select 2 or more text elements first, then click Add reflow.')}
        </div>
      </div>
    </div>
  );
}

export default React.memo(ReflowTooltip);
